const MS_PER_DAY = 24 * 60 * 60 * 1000;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONDAY = 1;
const WEDNESDAY = 3;
const FRIDAY = 5;
const SUNDAY = 0;

type TimeBand = 'EARLY_MORNING' | 'MORNING' | 'AFTERNOON' | 'EVENING' | 'NIGHT';

export const MORNING_POOL = ['Coffee and meinUnterricht time', 'Good morning'];
export const GENERIC_POOL = ['Nice to have you back', 'Welcome back', 'There you are again'];

// The ticket defines the time bands in CEST, so time-of-day is always derived
// in Europe/Berlin regardless of the device timezone.
const berlinFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Europe/Berlin',
  weekday: 'short',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  hourCycle: 'h23',
});

interface BerlinTime {
  weekday: number;
  minutesOfDay: number;
  weekIndex: number;
}

const getBerlinTime = (now: Date): BerlinTime => {
  const parts: Partial<Record<Intl.DateTimeFormatPartTypes, string>> = {};
  berlinFormatter.formatToParts(now).forEach(({ type, value }) => {
    parts[type] = value;
  });

  const daysSinceEpoch = Math.floor(
    Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)) / MS_PER_DAY,
  );

  return {
    weekday: WEEKDAYS.indexOf(parts.weekday as (typeof WEEKDAYS)[number]),
    minutesOfDay: Number(parts.hour) * 60 + Number(parts.minute),
    // Unix epoch day 0 (1970-01-01) was a Thursday, so day counts shifted by
    // +3 (Thu→Mon distance) divide into Monday-starting weeks — the rotation
    // index flips exactly at Monday 00:00 Berlin time.
    weekIndex: Math.floor((daysSinceEpoch + 3) / 7),
  };
};

const getTimeBand = (minutesOfDay: number): TimeBand => {
  if (minutesOfDay < 4 * 60 || minutesOfDay >= 22 * 60) return 'NIGHT';
  if (minutesOfDay < 6 * 60 + 30) return 'EARLY_MORNING';
  if (minutesOfDay < 12 * 60) return 'MORNING';
  if (minutesOfDay < 18 * 60) return 'AFTERNOON';
  return 'EVENING';
};

// Resolution order: day+time specials beat time-of-day pools; the generic
// pool only fills slots no other rule covers.
const getGreeting = (now: Date): string => {
  const { weekday, minutesOfDay, weekIndex } = getBerlinTime(now);
  const band = getTimeBand(minutesOfDay);

  if (weekday === MONDAY && band === 'MORNING') return 'New week, new lessons';
  if (weekday === WEDNESDAY && band === 'MORNING') return 'Half the week done';
  if (weekday === FRIDAY && band === 'AFTERNOON') return 'Almost weekend';
  if (weekday === SUNDAY && (band === 'MORNING' || band === 'AFTERNOON')) {
    return 'Sunday planning is half the battle';
  }
  if (weekday === SUNDAY && band === 'EVENING') {
    return 'Quickly prepare a lesson for Monday morning';
  }

  switch (band) {
    case 'EARLY_MORNING':
      return 'The early bird plans the lesson';
    case 'MORNING':
      return MORNING_POOL[weekIndex % MORNING_POOL.length];
    case 'EVENING':
      return 'Good evening';
    case 'NIGHT':
      return 'Happy night shift';
    default:
      return GENERIC_POOL[weekIndex % GENERIC_POOL.length];
  }
};

export default getGreeting;
